// Media Center — Arabic copy overrides (revision 2).
//
// This module mirrors the shape of `mediaData.js` and provides
// professionally adapted Arabic Modern Standard versions of every
// public-facing string on `/ar/media`. Non-string fields (photos,
// downloads, URLs, timeline years, brand names) come from the base
// module and are never translated.
//
// Editorial guidelines applied in this revision:
//   * Premium MSA with a natural media / entertainment cadence.
//   * "DJ SPY" is preserved as-is (never transliterated).
//   * Brand and platform names stay Latin (Spotify, Apple Music,
//     YouTube, Anghami, Instagram, TikTok, Snapchat, Telegram,
//     Beatport, DJSPYOfficial.com).
//   * Venue names stay in their official English form; only cities
//     and countries are localised via `VENUE_LOCATION_AR`.
//   * No unverifiable claims (no "first", "world-renowned", "most
//     recognizable", "legally licensed", direct comparisons with
//     Beatport / Apple Music / Spotify).
//   * "خلف الديكات" replaced with natural Arabic phrasing.
//   * Career-launch year corrected to 2026 (current project timeline).

export const TIMELINE_AR = [
  { year: "1999", title: "بداية المسيرة",             copy: "بدأ جوني عيد مسيرته الفنية باسم DJ SPY في المشهد الموسيقي والليلي اللبناني." },
  { year: "",    title: "التوسع الإقليمي",             copy: "امتدت عروضه لاحقاً إلى عدد من الدول والمدن في الشرق الأوسط وشمال إفريقيا." },
  { year: "",    title: "إقامات وعروض في النوادي",     copy: "قدّم عروضاً في نوادٍ وفعاليات في دبي وعمّان والدوحة والجيزة والمنامة ومدن أخرى في المنطقة." },
  { year: "",    title: "المهرجانات والفعاليات الكبرى", copy: "شارك في مهرجانات وحفلات وفعاليات إلى جانب مجموعة من الفنانين العرب." },
  { year: "",    title: "الإنتاج الموسيقي",             copy: "بدأ بإصدار أعمال أصلية وريميكسات وماشابات ومونتاجات مخصصة لاستخدام الـ DJs." },
  { year: "2026", title: "إطلاق DJSPYOfficial.com",   copy: "إطلاق المنصة الرسمية لـ DJ SPY لعرض وتحميل أعماله الموسيقية ومحتواه الإعلامي." },
  { year: "2026", title: "توسع المنصة الرقمية",       copy: "إضافة متجر الموسيقى، العضوية، المدونة، المركز الإعلامي، ومزايا مخصصة للجمهور والـ DJs." },
];

export const TIMELINE_DISCLAIMER_AR =
  "تعكس المحطات المذكورة أعلاه أبرز مراحل المسيرة الفنية. المراحل التي لا تحمل تاريخاً محدداً تُشير إلى فترات ممتدة لا إلى أعوام بعينها.";

export const ALONGSIDE_AR = [
  "الشاب خالد", "محمد رمضان", "ميريام فارس",
  "علي صابر",   "سيف نبيل",   "سعد لمجرد",
];
export const ALONGSIDE_DISCLAIMER_AR =
  "شارك DJ SPY المسرح نفسه مع فنانين عرب خلال مهرجانات وحفلات وفعاليات كبرى.";

export const GENRES_AR = [
  "EDM للمهرجانات", "أفرو هاوس", "ميلوديك تكنو",
  "موسيقى النوادي العربية", "مومباهتون", "ريغيتون",
];

// Section 10 — retitled to "أبرز الأرقام والمحطات" because the section
// mixes audience metrics with career milestones (not only awards).
export const RECOGNITION_AR = [
  { title: "جائزة YouTube Silver Creator",       copy: "تكريم من YouTube بعد وصول القناة إلى أكثر من 100 ألف مشترك." },
  { title: "أكثر من 100 ألف مشترك على YouTube",  copy: "محطة مهمة في مسيرة DJ SPY الرقمية." },
  { title: "أكثر من 2 مليون استماع على Anghami", copy: "إجمالي الاستماعات المسجلة لأعماله على المنصة." },
  { title: "أكثر من 600 عرض حي",                  copy: "عروض وفعاليات أقيمت على امتداد مسيرته الفنية." },
  { title: "أكثر من 20 عاماً من الخبرة",         copy: "مسيرة مستمرة في العروض الحية والإنتاج الموسيقي منذ عام 1999." },
  { title: "DJSPYOfficial.com",                    copy: "المنصة الرسمية لأعمال DJ SPY الموسيقية والإعلامية." },
];

export const PRESS_HEADLINE_AR = {
  "https://uaestories.com/johnny-eid-the-beatmaker-bridging-cultures/":
    "جوني عيد: صانع الإيقاع الذي يجسر بين الثقافات",
  "https://gulfmagazine.co/johnny-eid-the-journey-of-dj-spy/":
    "جوني عيد — مسيرة DJ SPY",
};

// Downloads — labels use " — " separators; English-only assets get a
// visible badge via `en_only: true` (rendered by DownloadCenter) so we
// never repeat "باللغة الإنجليزية" inside every note.
export const DOWNLOADS_AR = {
  bio_pdf:      { label: "السيرة الذاتية الرسمية — PDF",  note: "سيرة ذاتية تحريرية من صفحة واحدة.",         en_only: true },
  bio_docx:     { label: "السيرة الذاتية الرسمية — DOCX", note: "نسخة قابلة للتعديل للاستخدام التحريري والإعلامي.", en_only: true },
  press_kit:    { label: "الملف الصحفي الرسمي — PDF",      note: "ملف صحفي من ست صفحات يضم صوراً وأرقاماً واقتباسات.", en_only: true },
  fact_sheet:   { label: "ورقة المعلومات المختصرة — PDF",  note: "أبرز الأرقام في صفحة واحدة للمحررين.",       en_only: true },
  brand_guides: { label: "دليل الهوية البصرية — PDF",      note: "استخدام الشعار، المسافات، الألوان، والخطوط.", en_only: true },
  press_photos: { label: "الصور الصحفية الرسمية — ZIP",    note: "بورتريهات ولقطات أداء بدقة عالية." },
  logo_png:     { label: "شعار DJ SPY الرسمي — PNG",       note: "شعار أبيض شفاف بدقة عالية." },
};

export const DOWNLOAD_USAGE_NOTICE_AR =
  "يمكن للصحفيين والمروّجين والشركاء المعتمدين استخدام الأصول الرسمية لـ DJ SPY لأغراض تحريرية أو ترويج للفعاليات. لا يجوز التعديل الجوهري على الصور أو إعادة بيعها أو استخدامها لإيحاء بأي دعم دون إذن مسبق. الاعتماد المصور: DJ SPY Official.";

export const HERO_STATS_AR = [
  { value: "20+",   label: "سنة خبرة" },
  { value: "600+",  label: "عرض حي" },
  { value: "2M+",   label: "استماع على Anghami" },
  { value: "100K+", label: "مشترك على YouTube" },
];

// Section 04 — By the Numbers (Arabic). Independent from HERO_STATS_AR
// so admins can curate them separately in the Media Center CMS.
export const NUMBERS_AR = [
  { value: "20+",   label: "سنة خبرة",             copy: "" },
  { value: "600+",  label: "عرض حي",                copy: "" },
  { value: "2M+",   label: "استماع على Anghami",   copy: "" },
  { value: "100K+", label: "مشترك على YouTube",     copy: "" },
];

// Section 09 platform features (Arabic). Fallback when the CMS is
// off / empty so admin deletions can override this list.
export const PLATFORM_FEATURES_AR = [
  "متجر موسيقي احترافي للـ DJs",
  "تحميلات فورية",
  "ريميكسات وماشابات حصرية",
  "إنتاجات أصلية",
  "عضوية مميزة",
  "مدوّنة · مقالات محسّنة للبحث",
  "مكتبة موسيقية احترافية",
];

export const QUICK_FACTS_AR = [
  { label: "الاسم الفني",   value: "DJ SPY" },
  { label: "الاسم الحقيقي", value: "جوني عيد" },
  { label: "الجنسية",       value: "لبناني" },
  { label: "مقر الإقامة",   value: "دبي، الإمارات العربية المتحدة" },
  { label: "بداية المسيرة", value: "1999" },
  { label: "اللغات",        value: "العربية والإنجليزية" },
];

export const BIO_PARAGRAPHS_AR = [
  "جوني عيد، المعروف فنياً باسم DJ SPY، هو دي جي ومنتج موسيقي لبناني مقيم في دبي. بدأ مسيرته عام 1999، وطور على مدى أكثر من عقدين أسلوباً موسيقياً يجمع بين الموسيقى العربية المعاصرة والأصوات العالمية المستخدمة في النوادي والمهرجانات.",
  "تتنوع عروضه وإنتاجاته بين EDM، والأفرو هاوس، والميلوديك تكنو، والمومباهتون، والريغيتون، إلى جانب الريميكسات والماشابات العربية المصممة للعروض الحية.",
  "قدّم DJ SPY عروضاً في عدد من النوادي والفعاليات في الإمارات والمنطقة، من بينها Omni Dubai وStage Dubai وOmni Amman وOX Doha وOpal Giza وBD House Jeddah.",
  "كما أسس DJSPYOfficial.com، وهي منصة رقمية تعرض إنتاجاته الأصلية وريميكساته وماشاباته وأعماله المخصصة للـ DJs، مع ملفات موسيقية عالية الجودة وتجربة تحميل احترافية.",
];

export const VISION_HEADLINE_AR = "تطوير تجربة الموسيقى العربية للـ DJs حول العالم";
export const VISION_PARAGRAPHS_AR = [
  "يهدف DJSPYOfficial.com إلى توفير مساحة منظمة واحترافية لاكتشاف أعمال DJ SPY الأصلية وريميكساته وماشاباته ونسخه المخصصة للعروض الحية.",
  "تجمع المنصة الملفات الموسيقية عالية الجودة، والمعلومات الأساسية لكل عمل، وخيارات الاستماع والتحميل ضمن تجربة واحدة سهلة الاستخدام.",
  "الرؤية هي تقديم الموسيقى العربية المخصصة للنوادي والـ DJs بصورة أكثر تنظيماً واحترافية، والوصول بها إلى جمهور أوسع في المنطقة وحول العالم.",
];

export const STATS_DISCLAIMER_AR =
  "تعكس أرقام المسيرة السجلات المتاحة والمحطات الرسمية على المنصات حتى عام 2026 وقت النشر.";

// UI copy — every inline string inside the JSX. Kept separate from
// content constants above so the JSX imports only what it needs.
export const UI_AR = {
  siteName: "المركز الإعلامي",
  brandLine: "DJ SPY OFFICIAL · المركز الإعلامي",
  // Section-nav keeps only distinct sections; "الأرقام والمحطات" replaces
  // the earlier duplicated "الأرقام" + "الجوائز والتقدير" pair.
  navLabels: {
    biography:   "السيرة",
    career:      "المسيرة",
    numbers:     "الأرقام والمحطات",
    presence:    "الحضور الدولي",
    recognition: "الأرقام والمحطات",
    press:       "الصحافة",
    gallery:     "المعرض",
    downloads:   "التحميلات",
    bookings:    "الحجوزات",
  },
  languageSwitch: "English",
  ctaViewAssets: "استعرض الأصول الإعلامية",
  ctaMediaContact: "التواصل الإعلامي",
  englishBadge: "متوفر باللغة الإنجليزية",
  sections: {
    s01_hero:        "// DJ SPY OFFICIAL · المركز الإعلامي",
    s02_bio_eyebrow: "// SECTION 02",
    s02_bio_title:   "نبذة عن DJ SPY",
    s03_timeline_eyebrow: "// SECTION 03",
    s03_timeline_title:   "مسيرة فنية منذ عام 1999",
    s03_timeline_lede:    "أكثر من عقدين من العروض والإنتاج الموسيقي منذ عام 1999.",
    s04_numbers_eyebrow: "// SECTION 04",
    s04_numbers_title:   "أبرز الأرقام والمحطات",
    s05_presence_eyebrow: "// SECTION 05",
    s05_presence_title:   "حضور دولي",
    s05_presence_lede:    "عروض في مدن ودول متعددة، وأبرز نوادي المنطقة.",
    s06_alongside_eyebrow: "// SECTION 06",
    s06_alongside_title:   "أبرز العروض",
    s07_music_eyebrow: "// SECTION 07",
    s07_music_title:   "الأسلوب الموسيقي",
    s07_music_lede:    "إنتاجات أصلية · ريميكسات وماشابات حصرية · نسخ مخصصة للـ DJs.",
    s07_music_output_eyebrow: "// النتاج",
    s07_music_output_title:   "إنتاجات أصلية · ريميكسات وماشابات حصرية · نسخ مخصصة للـ DJs",
    s07_music_output_copy:    "كل عمل في كتالوج DJ SPY يُجهّز بما يناسب الاستخدام في العروض الحية، مع سرعة BPM واضحة، ومقدمات ونهايات ممتدة في الأعمال المخصصة للـ DJs، وبيانات موسيقية منظمة تسهّل إدماجها داخل السيت.",
    s08_vision_eyebrow: "// SECTION 08 · ملف",
    s09_platform_eyebrow: "// SECTION 09",
    s09_platform_title:   "DJSPYOfficial.com",
    s09_platform_lede_prefix: "المنصّة الرقمية الرسمية لأعمال DJ SPY الموسيقية والإعلامية — متاحة على ",
    s09_platform_features_eyebrow: "// ميزات المنصّة",
    s09_platform_features: [
      "متجر موسيقي احترافي للـ DJs",
      "تحميلات فورية",
      "ريميكسات وماشابات حصرية",
      "إنتاجات أصلية",
      "عضوية مميزة",
      "مدوّنة · مقالات محسّنة للبحث",
      "مكتبة موسيقية احترافية",
    ],
    s10_recognition_eyebrow: "// SECTION 10",
    s10_recognition_title:   "أبرز الأرقام والمحطات",
    s11_verified_eyebrow: "// SECTION 11",
    s11_verified_title:   "المنصات الرسمية",
    s11_verified_lede:    "منصّات DJ SPY الرسمية — للصحفيين وفرق التحقّق.",
    s12_press_eyebrow: "// SECTION 12",
    s12_press_title:   "الصحافة والتغطية الإعلامية",
    s12_press_lede:    "مختارات من المقالات والتغطيات في الصحافة الإقليمية. اضغط على أي مقال لقراءته على مصدره الأصلي.",
    s12_press_read: "قراءة المقال",
    s13_gallery_eyebrow: "// SECTION 13",
    s13_gallery_title:   "معرض الصور",
    s13_gallery_lede:    "صور بدقة عالية، مُتاحة للاستخدام التحريري مع ذكر المصدر.",
    s13_gallery_categories: {
      "Editorial Portraits": "بورتريهات تحريرية",
      "Live Performances":   "عروض حية",
      "Behind The Booth":    "خلف أجهزة الـ DJ",
      "Festival Moments":    "لحظات من المهرجانات",
    },
    s14_downloads_eyebrow: "// SECTION 14",
    s14_downloads_title:   "مركز التحميل",
    s14_downloads_lede:    "كل ما يحتاجه الصحفي، المروّج، أو منظّم الفعالية — في صفحة واحدة، وبضغطة واحدة.",
    s15_news_eyebrow: "// SECTION 15",
    s15_news_title:   "آخر الأخبار",
    s15_news_lede:    "أحدث ما ينشره فريق DJ SPY التحريري.",
    s16_bookings_eyebrow: "// SECTION 16",
    s16_bookings_title:   "الحجوزات والتواصل الإعلامي",
    s16_bookings_lede:    "متاح للحجوزات داخل الإمارات وخارجها. لحجوزات المهرجانات، الشراكات التجارية، والتغطيات الصحفية.",
  },
  labels: {
    quickFacts:      "// حقائق سريعة",
    featuredVenue:   "// قاعة مميّزة",
    comingSoon:      "قريباً",
    unavailable:     "// غير متاح حالياً",
    unavailableCopy: "تعذّر الوصول إلى دليل القاعات في الوقت الحالي.",
    noVenues:        "// لا توجد قاعات معروضة حالياً",
    noVenuesCopy:    "ستظهر القاعات هنا فور إضافتها.",
    directChannels:  "// قنوات مباشرة",
    email:           "البريد الإلكتروني",
    telegram:        "Telegram",
    instagram:       "Instagram",
    tiktok:          "TikTok",
    website:         "الموقع الرسمي",
    output:          "// النتاج",
    platformFeatures:"// ميزات المنصّة",
  },
  booking: {
    thanks_eyebrow: "// تم استلام الطلب",
    thanks_title:   "شكراً لك.",
    thanks_copy:    "شكراً لتواصلك مع إدارة DJ SPY. تم استلام طلبك بنجاح، وسيراجع فريقنا التفاصيل ويتواصل معك في أقرب وقت ممكن.",
    ph_name:        "الاسم الكامل *",
    ph_company:     "اسم الشركة أو الجهة",
    ph_email:       "البريد الإلكتروني",
    ph_phone:       "رقم الهاتف أو واتساب",
    ph_date:        "تاريخ الفعالية *",
    ph_city:        "المدينة والدولة *",
    ph_venue:       "اسم المكان",
    ph_event_type:  "نوع الفعالية",
    ph_attendance:  "العدد المتوقع للحضور",
    ph_duration:    "مدة العرض المطلوبة",
    ph_budget:      "الميزانية التقديرية (اختياري)",
    ph_message:     "تفاصيل إضافية — أخبرنا عن الفعالية، الجمهور، والتنسيق… *",
    consent:        "أوافق على أن تتواصل معي إدارة DJ SPY بخصوص هذا الطلب.",
    submit:         "إرسال الطلب",
    submitting:     "جارٍ الإرسال…",
    err_name:       "يرجى إدخال الاسم الكامل.",
    err_contact:    "يرجى إدخال البريد الإلكتروني أو رقم الهاتف على الأقل.",
    err_email:      "صيغة البريد الإلكتروني غير صحيحة.",
    err_date:       "يرجى إدخال تاريخ الفعالية (تقديري كافٍ).",
    err_city:       "يرجى إدخال المدينة والدولة.",
    err_message:    "يرجى كتابة تفاصيل مختصرة عن الفعالية.",
    err_consent:    "يرجى تأكيد الموافقة على التواصل.",
    toast_ok:       "تم استلام الطلب.",
    toast_err:      "تعذّر إرسال الطلب.",
  },
  hero: {
    roles: ["دي جي", "منتج موسيقي", "مؤسس DJSPYOfficial.com"],
  },
  footerLine: "DJ SPY · متاح للحجوزات داخل الإمارات وخارجها",
  copyright:  "© 2026 DJ SPY. جميع الحقوق محفوظة.",
};

// Controlled Arabic display mapping for venue city/country when the
// Trusted-By admin panel doesn't yet supply Arabic values. Venue brand
// names remain Latin — never invent Arabic venue names here.
export const VENUE_LOCATION_AR = {
  countries: {
    "United Arab Emirates": "الإمارات العربية المتحدة",
    "Saudi Arabia":         "المملكة العربية السعودية",
    "Qatar":                "قطر",
    "Jordan":               "الأردن",
    "Bahrain":              "البحرين",
    "Egypt":                "مصر",
    "Lebanon":              "لبنان",
  },
  cities: {
    "Dubai":     "دبي",
    "Abu Dhabi": "أبوظبي",
    "Jeddah":    "جدة",
    "Doha":      "الدوحة",
    "Amman":     "عمّان",
    "Manama":    "المنامة",
    "Giza":      "الجيزة",
    "Cairo":     "القاهرة",
    "Beirut":    "بيروت",
  },
};
